import { useCallback, useMemo } from "react";

import { useParams } from "react-router";
import { Add } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, Grid2 as Grid, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import Paper from "@mui/material/Paper";
import { Box, Stack } from "@mui/system";

import { attributionGroupsText, globalText } from "../../../assets/texts";
import { allocationTxt } from "../../../assets/texts/CloudAnalytics/allocation";
import { useAuthContext } from "../../../Context/AuthContext";
import useSegmentTrackEvent from "../../../utils/useSegmentTrackEvent";
import { RuleDivider } from "./components/RuleDivider";
import useAllocationDndStore from "./hooks/useAllocationDnd";
import { trackAllocationEvent } from "./utils";

export const UnallocatedCosts = ({ disabled }: { disabled?: boolean }) => {
  const { items, setHasUnallocated, setUnallocated, unallocatedCosts, hasUnallocated, fieldErrors, isGroup, addItem } =
    useAllocationDndStore();
  const { trackEvent } = useSegmentTrackEvent();
  const { allocationId } = useParams<{ allocationId: string }>();

  const { currentUser } = useAuthContext({ mustHaveUser: true });
  const canRemove = useMemo(() => items.main.length === 1 && !isGroup, [isGroup, items.main.length]);

  const handleAddAllocation = useCallback(() => {
    addItem(crypto.randomUUID());
    trackAllocationEvent({
      trackEvent,
      eventName: "Allocation Rule Added",
      ruleCount: items.main.length,
      hasUnallocated,
      allocationId,
      currentUserEmail: currentUser.email,
    });
  }, [addItem, trackEvent, items.main.length, hasUnallocated, allocationId, currentUser.email]);

  const handleRemove = useCallback(() => {
    setHasUnallocated(false);
  }, [setHasUnallocated]);

  const handleOnChange = useCallback(
    (e) => {
      setUnallocated(e.target.value);
    },
    [setUnallocated]
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        border: "none",
        backgroundColor: "transparent",
        position: "relative",
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ width: "100%" }}>
        <RuleDivider isLast />
        <Box sx={{ ml: 7, pt: 5.5, width: "100%" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="body1">{allocationTxt.ALLOCATION_END_SEPARATOR}</Typography>
            <Stack direction="row" alignItems="center" gap={2}>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  size="medium"
                  disabled={disabled}
                  startIcon={<Add />}
                  onClick={handleAddAllocation}
                >
                  {allocationTxt.ADD_ALLOCATION}
                </Button>
              </Box>
              {canRemove && (
                <Grid size={1} container justifyContent="flex-end" alignContent="center">
                  <Tooltip title={globalText.REMOVE}>
                    <IconButton onClick={handleRemove} size="medium">
                      <DeleteIcon fontSize="small" color="action" sx={{ pointerEvents: "none" }} />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
            </Stack>
          </Stack>
          <Box>
            <TextField
              name="title"
              variant="outlined"
              label="Unallocated"
              required
              disabled={disabled}
              value={unallocatedCosts}
              onChange={handleOnChange}
              error={!!fieldErrors.unallocatedCosts}
              helperText={fieldErrors.unallocatedCosts}
              placeholder={attributionGroupsText.UNALLOCATED}
              sx={({ palette }) => ({
                maxWidth: 540,
                width: "100%",
                backgroundColor: palette.general.backgroundDefault,
              })}
            />
            <Typography color="text.secondary" fontSize={12}>
              {allocationTxt.UNALLOCATED_COSTS_HELPER_TEXT}
            </Typography>
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
};
